/**
 * @author Martin Leonov <martinleonov@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */
import isNil from 'lodash/isNil';

import {
  MagazinePlaylist,
  Playlist,
  ShufflePlaylist,
  Track as SimulatorTrack
} from './deterministic';
import IcalPlanner from '../ical/ical-planner';
import ScheduleEnum from '../schedule/schedule-enum';

/**
 * Třída usnadňující mapování na datové modely vhodné pro Simulator.
 * @implements {KeyedClipIteratorFactory}
 */
export class KeyedClipIteratorFactory {
  /**
   * Vytvoří novou instanci.
   * @param {string} id Identifikátor playlistu.
   * @param {ClipIteratorFactory} itf Iterátor
   * @returns {KeyedClipIteratorFactory} Nová instance.
   */
  constructor(id, itf) {
    this.id = id;
    this.itf = itf;
  }

  /** @inheritdoc */
  key() {
    return this.id;
  }

  /** @inheritdoc */
  get global() {
    return this.itf.global;
  }

  /** @inheritdoc */
  newIterator(t) {
    return this.itf.newIterator(t);
  }

  /** @inheritdoc */
  valid(t) {
    return this.itf.valid(t);
  }

  /** @inheritdoc */
  currentOrNextValid(t, maxTime) {
    return this.itf.currentOrNextValid(t, maxTime);
  }

  /**
   * Funkce která namapuje Playlist na KeyedClipIteratorFactory.
   * @param {Playlist} playlist Playlist, který se bude mapovat.
   * @param {TrackGetter} tracksGetter Funkce pro získání tracků z playlistu.
   * @returns {KeyedClipIteratorFactory} Namapovaný playlist do KeyedClipIteratorFactory.
   */
  static fromPlaylist(playlist, tracksGetter) {
    const { id, scope, cut, scheduleEnum } = playlist;
    const playlistTracks = tracksGetter(id);
    const tracks = playlistTracks.map((t) =>
      KeyedClipIteratorFactory.fromTrack(t, tracksGetter)
    );
    let p;
    switch (scheduleEnum) {
      case ScheduleEnum.RANDOM:
        p = new ShufflePlaylist(...tracks);
        break;
      case ScheduleEnum.STATIC:
      case ScheduleEnum.DYNAMIC:
      default:
        p = new Playlist(...tracks);
        break;
    }

    let clipIteratorFactory = p;
    if (scope === 'application' && cut > 0) {
      clipIteratorFactory = new MagazinePlaylist(cut, p);
    }

    return new KeyedClipIteratorFactory(id, clipIteratorFactory);
  }

  /**
   * Funkce, která vytvoří validator který určí zda je track platný,
   * nebo kdy bude platný.
   * @param {Track} track Track pro který se má zjišťovat validita.
   * @returns {TrackValidator} Validátor pro track.
   */
  static validator(track) {
    const icalPlanner = new IcalPlanner();
    return {
      valid: (t) =>
        icalPlanner.hasEventOnDate(track.planIcal, new Date(t / 1000)),
      currentOrNextValid: (t, maxTime) => {
        const date = icalPlanner.currentOrNextValid(
          track.planIcal,
          new Date(t / 1000),
          new Date(maxTime / 1000)
        );
        if (isNil(date)) {
          return maxTime;
        }
        return date.getTime() * 1000;
      }
    };
  }

  /**
   * Funkce, která převede PlayerLighter Track model na model vhodný pro Simulator.
   * @param {Track} track PlayerLighter model.
   * @param {TracksGetter} tracksGetter Funkce pro získání tracků z playlistu.
   * @returns {SimulatorTrack} Model tracku pro Simulator.
   */
  static fromTrack(track, tracksGetter) {
    let validator = null;
    if (!isNil(track.planIcal)) {
      validator = KeyedClipIteratorFactory.validator(track);
    }
    const { childPlaylist } = track;
    if (childPlaylist) {
      // Jedná se o vnitřní deterministický playlist
      if (childPlaylist.deterministic) {
        return new SimulatorTrack(
          { playlist: childPlaylist },
          track.playDuration * 1000,
          track.volume,
          validator
        );
      }

      // Obyčejný vnitřní playlist
      const clipIteratorFactory = KeyedClipIteratorFactory.fromPlaylist(
        childPlaylist,
        tracksGetter
      );
      return new SimulatorTrack(
        clipIteratorFactory,
        track.playDuration * 1000,
        track.volume,
        validator
      );
    }

    const { medium } = track;
    // Délka media v tracku je 0, pak zkusím převzít fyzickou délku
    if (track.playDuration === 0 && medium.params?.physicalDuration) {
      track.playDuration = medium.params.physicalDuration * 1000;
    }

    return new SimulatorTrack(
      { medium },
      track.playDuration * 1000,
      track.volume,
      validator
    );
  }
}

/**
 * Vrátí pole tracků patřících do zadaného playlistu.
 * @name TracksGetter
 * @function
 * @param {String} playlistId Indentifkátor plalistu.
 * @returns {Array.<Track>} Pole nalezených tracků.
 */
